/**
 * Inputs: a trip plus an event or booking the owner wants to move or remove.
 * Outputs: the same trip with diary dates synced, or that record gone.
 * Payments and refunds are never deleted. A booking with money history
 * cannot be removed; cancel it or record a refund instead.
 *
 * curl -b cookies -X PUT $HOST/api/trip -H "Origin: $HOST" -H "Content-Type: application/json" \
 *   -d '{"trip":{...moved or removed...},"etag":"..."}'
 */

import { bookingHasFinancialHistory } from "./financial.ts";
import { bookingDisplayTitle } from "./upcoming.ts";
import { formatLongDate } from "./time.ts";
import type { Booking, Idea, TripDocument, TripEvent } from "./schema.ts";

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export type LifecycleOk = { ok: true; doc: TripDocument; summary: string };
export type LifecycleErr = { ok: false; error: string };
export type LifecycleResult = LifecycleOk | LifecycleErr;

export function isTripDate(value: string): boolean {
  return DATE_ONLY.test(value);
}

export function eventsForBooking(doc: TripDocument, bookingId: string): TripEvent[] {
  return doc.events.filter((event) => event.bookingId === bookingId && !event.archived);
}

export function bookingDiaryDates(
  doc: TripDocument,
  booking: Booking,
): { startDate: string | null; endDate: string | null } {
  const linked = eventsForBooking(doc, booking.id).map((event) => event.date).filter(Boolean).sort();
  if (linked.length) {
    return { startDate: linked[0] ?? null, endDate: linked[linked.length - 1] ?? null };
  }
  return { startDate: booking.startDate ?? null, endDate: booking.endDate ?? null };
}

function dateInTrip(doc: TripDocument, date: string): string | null {
  if (!isTripDate(date)) return "Choose a calendar day.";
  if (date < doc.trip.startDate || date > doc.trip.endDate) {
    return "That day is outside this trip.";
  }
  return null;
}

export function syncLinkedBookingDates(doc: TripDocument, bookingId: string | null | undefined): TripDocument {
  if (!bookingId) return doc;
  const booking = doc.bookings.find((row) => row.id === bookingId);
  if (!booking) return doc;
  if (booking.kind === "hotel_reservation" || booking.kind === "flight") return doc;
  const dates = bookingDiaryDates(doc, booking);
  if (!dates.startDate) return doc;
  return {
    ...doc,
    bookings: doc.bookings.map((row) => (
      row.id === bookingId
        ? { ...row, startDate: dates.startDate, endDate: dates.endDate ?? dates.startDate }
        : row
    )),
  };
}

export function moveEventToDate(doc: TripDocument, eventId: string, nextDate: string): LifecycleResult {
  const rangeError = dateInTrip(doc, nextDate);
  if (rangeError) return { ok: false, error: rangeError };
  const event = doc.events.find((row) => row.id === eventId);
  if (!event) return { ok: false, error: "That plan is not on the trip." };
  if (event.kind === "flight" || event.kind === "stay_window") {
    return { ok: false, error: "Move a flight or hotel from its own details, not as a day plan." };
  }
  if (event.date === nextDate) {
    return { ok: true, doc, summary: `Already on ${formatLongDate(nextDate)}.` };
  }
  const events = doc.events.map((row) => (
    row.id === eventId
      ? { ...row, date: nextDate, dateConfidence: "owner_confirmed", archived: false }
      : row
  ));
  const next = syncLinkedBookingDates({ ...doc, events }, event.bookingId);
  return {
    ok: true,
    doc: next,
    summary: `Moved to ${formatLongDate(nextDate)}.`,
  };
}

export function moveIdeaToDate(doc: TripDocument, ideaId: string, nextDate: string): LifecycleResult {
  const rangeError = dateInTrip(doc, nextDate);
  if (rangeError) return { ok: false, error: rangeError };
  const idea = doc.ideas.find((row) => row.id === ideaId);
  if (!idea) return { ok: false, error: "That idea is not on the trip." };
  if (idea.date === nextDate) {
    return { ok: true, doc, summary: `Already on ${formatLongDate(nextDate)}.` };
  }
  const ideas = doc.ideas.map((row) => (row.id === ideaId ? { ...row, date: nextDate } : row));
  return {
    ok: true,
    doc: { ...doc, ideas },
    summary: `Moved to ${formatLongDate(nextDate)}.`,
  };
}

export type RemovalPreview = {
  title: string;
  body: string;
  confirmLabel: string;
  blocked: string | null;
  removesBooking: boolean;
  removesEventCount: number;
};

function bookingTitle(doc: TripDocument, booking: Booking): string {
  return bookingDisplayTitle(booking, doc, { includeHousehold: false });
}

export function previewEventRemoval(doc: TripDocument, eventId: string): RemovalPreview | { error: string } {
  const event = doc.events.find((row) => row.id === eventId && !row.archived);
  if (!event) return { error: "That plan is not on the trip." };
  const label = event.title ?? event.kind;
  const booking = event.bookingId ? doc.bookings.find((row) => row.id === event.bookingId) : undefined;
  const siblings = booking ? eventsForBooking(doc, booking.id).filter((row) => row.id !== event.id) : [];
  if (booking && siblings.length === 0 && bookingHasFinancialHistory(doc, booking.id)) {
    return {
      title: `Remove ${label}?`,
      body: "A payment or refund is recorded on this booking. Mark the plan cancelled, or record a refund. Money history is not deleted.",
      confirmLabel: "Remove from the trip",
      blocked: "This booking has a payment or refund recorded.",
      removesBooking: false,
      removesEventCount: 0,
    };
  }
  const removesBooking = Boolean(booking && siblings.length === 0);
  const extra = removesBooking
    ? " The Bookings record goes with it."
    : booking
      ? " The shared package cost stays, because another plan still uses it."
      : "";
  return {
    title: `Remove ${label}?`,
    body: `This takes it off ${formatLongDate(event.date)}.${extra} You can restore an earlier revision from Edit if this was a mistake.`,
    confirmLabel: "Yes, remove it",
    blocked: null,
    removesBooking,
    removesEventCount: 1,
  };
}

export function previewIdeaRemoval(idea: Idea): RemovalPreview {
  return {
    title: `Remove ${idea.title}?`,
    body: idea.date
      ? `This takes the idea off ${formatLongDate(idea.date)}. It is not a booking.`
      : "This takes the idea off the list. It is not a booking.",
    confirmLabel: "Yes, remove it",
    blocked: null,
    removesBooking: false,
    removesEventCount: 0,
  };
}

export function previewBookingRemoval(doc: TripDocument, bookingId: string): RemovalPreview | { error: string } {
  const booking = doc.bookings.find((row) => row.id === bookingId);
  if (!booking) return { error: "That booking is not on the trip." };
  const title = bookingTitle(doc, booking);
  if (bookingHasFinancialHistory(doc, booking.id)) {
    return {
      title: `Remove ${title}?`,
      body: "A payment or refund is recorded. Mark the booking cancelled, or record a refund. Money history is not deleted.",
      confirmLabel: "Remove booking",
      blocked: "This booking has a payment or refund recorded.",
      removesBooking: false,
      removesEventCount: 0,
    };
  }
  const linked = eventsForBooking(doc, booking.id);
  if (booking.kind === "hotel_reservation") {
    return {
      title: `Remove ${title}?`,
      body: "This removes the reservation from Bookings. The stay chapter on Journey stays. You can restore an earlier revision from Edit if this was a mistake.",
      confirmLabel: "Yes, remove this reservation",
      blocked: null,
      removesBooking: true,
      removesEventCount: 0,
    };
  }
  if (booking.kind === "flight") {
    return {
      title: `Remove ${title}?`,
      body: "This removes the cost record from Bookings. The flight stays on the journey.",
      confirmLabel: "Yes, remove this cost record",
      blocked: null,
      removesBooking: true,
      removesEventCount: 0,
    };
  }
  const days = linked.map((event) => formatLongDate(event.date)).join(" and ");
  const eventBit = linked.length
    ? ` It also removes ${linked.length === 1 ? "the plan" : `${linked.length} diary plans`}${days ? ` (${days})` : ""}.`
    : "";
  return {
    title: `Remove ${title}?`,
    body: `This removes the booking from the family trip.${eventBit} You can restore an earlier revision from Edit if this was a mistake.`,
    confirmLabel: "Yes, remove it",
    blocked: null,
    removesBooking: true,
    removesEventCount: linked.length,
  };
}

export function removeEventFromTrip(doc: TripDocument, eventId: string): LifecycleResult {
  const preview = previewEventRemoval(doc, eventId);
  if ("error" in preview) return { ok: false, error: preview.error };
  if (preview.blocked) return { ok: false, error: preview.blocked };
  const event = doc.events.find((row) => row.id === eventId);
  if (!event) return { ok: false, error: "That plan is not on the trip." };
  const events = doc.events.filter((row) => row.id !== eventId);
  let bookings = doc.bookings;
  if (preview.removesBooking && event.bookingId) {
    bookings = doc.bookings.filter((row) => row.id !== event.bookingId);
  }
  const next = event.bookingId && !preview.removesBooking
    ? syncLinkedBookingDates({ ...doc, events, bookings }, event.bookingId)
    : { ...doc, events, bookings };
  return { ok: true, doc: next, summary: "Removed from the trip." };
}

export function removeIdeaFromTrip(doc: TripDocument, ideaId: string): LifecycleResult {
  if (!doc.ideas.some((row) => row.id === ideaId)) {
    return { ok: false, error: "That idea is not on the trip." };
  }
  return {
    ok: true,
    doc: { ...doc, ideas: doc.ideas.filter((row) => row.id !== ideaId) },
    summary: "Removed from the trip.",
  };
}

export function removeBookingFromTrip(doc: TripDocument, bookingId: string): LifecycleResult {
  const preview = previewBookingRemoval(doc, bookingId);
  if ("error" in preview) return { ok: false, error: preview.error };
  if (preview.blocked) return { ok: false, error: preview.blocked };
  const booking = doc.bookings.find((row) => row.id === bookingId);
  if (!booking) return { ok: false, error: "That booking is not on the trip." };
  const removeEventIds = new Set(
    booking.kind === "hotel_reservation" || booking.kind === "flight"
      ? []
      : eventsForBooking(doc, bookingId).map((event) => event.id),
  );
  return {
    ok: true,
    doc: {
      ...doc,
      bookings: doc.bookings.filter((row) => row.id !== bookingId),
      events: doc.events
        .filter((row) => !removeEventIds.has(row.id))
        .map((row) => (row.bookingId === bookingId ? { ...row, bookingId: null } : row)),
    },
    summary: "Removed from the trip.",
  };
}
